/** Local profile media pipeline — SPEC §14 / SEC-MED-003.
 * Photos are cover-cropped and downscaled to a 256px square before storage;
 * GIFs and short videos pass through byte-identical after strict validation. */

import { useEffect, useState } from "react";
import {
  PROFILE_AVATAR_PIXELS,
  PROFILE_VIDEO_MAX_SECONDS,
  validateProfileMediaFile,
  type ProfileMedia,
  type ProfileMediaKind,
} from "@veto/protocol";

function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function blobToDataB64(blob: Blob): Promise<string> {
  return blob.arrayBuffer().then((buf) => bytesToB64(new Uint8Array(buf)));
}

export function dataB64ToBlob(dataB64: string, mime: string): Blob {
  const bin = atob(dataB64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export function profileMediaToBlob(media: ProfileMedia): Blob {
  return dataB64ToBlob(media.dataB64, media.mime);
}

export function profileMediaToBlobUrl(media: ProfileMedia): string {
  return URL.createObjectURL(profileMediaToBlob(media));
}

/** Cover-crop an image to a square PROFILE_AVATAR_PIXELS bitmap via canvas. */
async function downscaleImageSquare(file: Blob): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = PROFILE_AVATAR_PIXELS;
  canvas.height = PROFILE_AVATAR_PIXELS;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable for photo processing");
  let source: CanvasImageSource;
  let sw: number;
  let sh: number;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    source = bitmap;
    sw = bitmap.width;
    sh = bitmap.height;
  } catch {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Could not read image"));
      el.src = URL.createObjectURL(file);
    });
    source = img;
    sw = img.naturalWidth;
    sh = img.naturalHeight;
  }
  try {
    const side = Math.max(1, Math.min(sw, sh));
    const sx = (sw - side) / 2;
    const sy = (sh - side) / 2;
    ctx.drawImage(source, sx, sy, side, side, 0, 0, PROFILE_AVATAR_PIXELS, PROFILE_AVATAR_PIXELS);
    const webp = await new Promise<Blob | null>((r) =>
      canvas.toBlob(r, "image/webp", 0.85),
    );
    if (webp && webp.type === "image/webp") return webp;
    const jpeg = await new Promise<Blob | null>((r) =>
      canvas.toBlob(r, "image/jpeg", 0.85),
    );
    if (jpeg) return jpeg;
    throw new Error("Could not encode processed photo");
  } finally {
    bitmap?.close();
  }
}

/** Probe duration/dimensions of a candidate video avatar (≤ PROFILE_VIDEO_MAX_SECONDS). */
function probeVideo(file: Blob): Promise<{ durationSec: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true;
    video.preload = "metadata";
    const cleanup = () => URL.revokeObjectURL(url);
    const fail = (message: string) => {
      cleanup();
      reject(new Error(message));
    };
    const timer = window.setTimeout(() => fail("Could not read video metadata"), 8000);
    video.onloadedmetadata = () => {
      window.clearTimeout(timer);
      const durationSec = video.duration;
      if (!Number.isFinite(durationSec) || durationSec <= 0) {
        fail("Unsupported video stream");
        return;
      }
      if (durationSec > PROFILE_VIDEO_MAX_SECONDS + 0.25) {
        fail(`Video must be ${PROFILE_VIDEO_MAX_SECONDS}s or shorter`);
        return;
      }
      if (!video.videoWidth || !video.videoHeight) {
        fail("Unsupported video stream");
        return;
      }
      cleanup();
      resolve({ durationSec, width: video.videoWidth, height: video.videoHeight });
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      fail("This video format can't be used as an avatar");
    };
    video.src = url;
  });
}

/**
 * Validate + process a picked avatar file into sealed-ready ProfileMedia.
 * Throws Error with a user-facing message on rejection.
 */
export async function processProfileMediaFile(file: File): Promise<ProfileMedia> {
  const check = validateProfileMediaFile({
    fileName: file.name,
    mediaType: file.type,
    size: file.size,
  });
  if (!check.ok) throw new Error(check.message);
  const kind: ProfileMediaKind = check.kind;

  if (kind === "image") {
    const blob = await downscaleImageSquare(file);
    return { kind, mime: blob.type, dataB64: await blobToDataB64(blob), updatedAt: Date.now() };
  }

  if (kind === "video") {
    await probeVideo(file);
    return {
      kind,
      mime: (file.type || "video/mp4").toLowerCase().split(";")[0]!,
      dataB64: await blobToDataB64(file),
      updatedAt: Date.now(),
    };
  }

  // GIF — pass bytes through untouched (animation must survive)
  return {
    kind,
    mime: "image/gif",
    dataB64: await blobToDataB64(file),
    updatedAt: Date.now(),
  };
}

/** Object URL lifecycle for a ProfileMedia (auto-revokes on change/unmount). */
export function useProfileMediaUrl(media: ProfileMedia | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!media?.dataB64) {
      setUrl(null);
      return;
    }
    const next = profileMediaToBlobUrl(media);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
    };
  }, [media?.dataB64, media?.mime]);
  return url;
}
